// The one date entry control. Type the digits — 12052029 becomes 12/05/2029 — or
// pick from the calendar; both produce the same ISO value the API expects.
//
// WHY THIS IS NOT <input type="date">
// The native control cannot be typed into freely: its value must already be a valid
// 'YYYY-MM-DD', so there is no way to express "the user is halfway through a date".
// Reaching a date a year out meant paging a calendar. This is a text box that masks
// digits into dd/mm/yyyy, with the native picker kept alongside it — typing is an
// addition, not a replacement.
//
// THE RULE THAT MAKES IT SAFE: A HALF-TYPED DATE IS NEVER WRITTEN ANYWHERE.
// While the draft is incomplete (fewer than 8 digits) or impossible (31/02/2029),
// this component emits NOTHING — the form keeps the value it already had — and
// reports itself unfinished to the surrounding <Modal>, which blocks Save and names
// the field. Both halves are required. Emitting '' instead would be worse than
// useless: expenses, payments, settlements, publications and invoices all read a
// missing date as `|| today()`, so a half-typed date would save as TODAY — a real,
// plausible, wrong date. And blocking nothing would save the PREVIOUS date while the
// box shows a different half-typed one. Neither failure is visible after the fact.
//
// Editing an existing date works the same way: focusing selects the whole value, so
// eight digits replace it outright, and the stored date is not touched until the new
// one is complete and real.
import { useContext, useEffect, useId, useRef, useState } from 'react';
import he from '../i18n/he';
import { FieldValidityContext } from './FormValidity';

const LEN = 8; // ddmmyyyy

const onlyDigits = (s) => String(s).replace(/\D/g, '').slice(0, LEN);

// Could these digits still grow into a real date? Checked per keystroke so an
// impossible value never appears on screen at all — a day of 4x or a month of 13 is
// refused as it is typed rather than accepted and flagged afterwards.
const canStillBeADate = (d) => {
  if (d.length >= 1 && Number(d[0]) > 3) return false;                       // day  4x..9x
  if (d.length >= 2 && (Number(d.slice(0, 2)) < 1 || Number(d.slice(0, 2)) > 31)) return false;
  if (d.length >= 3 && Number(d[2]) > 1) return false;                       // month 2x..9x
  if (d.length >= 4 && (Number(d.slice(2, 4)) < 1 || Number(d.slice(2, 4)) > 12)) return false;
  // A 2-digit year is REJECTED, never expanded: this system tracks contracts years
  // out, and guessing a century is where a silent wrong answer costs most. Requiring
  // a leading 1 or 2 keeps '29' from ever looking like a finished year.
  if (d.length >= 5 && (Number(d[4]) < 1 || Number(d[4]) > 2)) return false;
  return true;
};

// dd/mm/yyyy with separators only BETWEEN groups — never a trailing one. A trailing
// '/' would be re-added the instant backspace removed it, so the key would appear
// dead at exactly the two points in the string a correction usually starts from.
const format = (d) => {
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
};

export const isoToDigits = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  return m ? `${m[3]}${m[2]}${m[1]}` : '';
};

// Complete digits → ISO, or null when they do not name a real day. The round-trip
// through Date is what rejects 31/02: it never rolls forward into March here, because
// a date the user did not type is a date they cannot see is wrong.
export const digitsToIso = (d) => {
  if (d.length !== LEN) return null;
  const day = Number(d.slice(0, 2));
  const month = Number(d.slice(2, 4));
  const year = Number(d.slice(4));
  const dt = new Date(Date.UTC(year, month - 1, day));
  if (dt.getUTCFullYear() !== year || dt.getUTCMonth() !== month - 1 || dt.getUTCDate() !== day) {
    return null;
  }
  return `${d.slice(4)}-${d.slice(2, 4)}-${d.slice(0, 2)}`;
};

// The control without a label wrapper, for rows that lay out their own fields.
export function DateControl({ value, onChange, name, label, ...rest }) {
  const id = useId();
  const registry = useContext(FieldValidityContext);
  const [digits, setDigits] = useState(() => isoToDigits(value));
  // What this field last put into form state. The comparison against it is what tells
  // an OUTSIDE change (a form reset, a modal reopened on another record) apart from
  // the echo of our own emit, which must not clobber what is being typed.
  const lastEmitted = useRef(value || '');

  useEffect(() => {
    const incoming = value || '';
    if (incoming !== lastEmitted.current) {
      lastEmitted.current = incoming;
      setDigits(isoToDigits(incoming));
    }
  }, [value]);

  const iso = digitsToIso(digits);
  const unfinished = digits.length > 0 && !iso;
  const state = digits.length < LEN ? 'incomplete' : 'unreal';

  // Register with the surrounding Modal so Save can be blocked and the field named.
  // No provider (a control outside a modal) simply means no gating — the inline error
  // below still shows, and nothing here depends on the registry existing.
  useEffect(() => {
    if (!registry) return undefined;
    registry.report(id, unfinished ? label || he.common.dateFallbackLabel : null);
    return () => registry.report(id, null);
  }, [registry, id, unfinished, label]);

  const commit = (next) => {
    // Clearing the box is a complete act and means "no date". Anything short of a real
    // date is not, and leaves form state exactly as it was.
    const out = next.length === 0 ? '' : digitsToIso(next);
    if (out == null || out === lastEmitted.current) return;
    lastEmitted.current = out;
    onChange({ target: { value: out, name } });
  };

  const handleType = (e) => {
    const raw = e.target.value;
    // A pasted ISO date ('2029-05-12', copied from anywhere else in the app) would
    // otherwise read as the digits 20290512 and be refused at the month. The display
    // only ever contains '/', so an ISO shape can only have arrived by paste.
    const pastedIso = /^\d{4}-\d{2}-\d{2}$/.test(raw) ? isoToDigits(raw) : null;
    const next = pastedIso || onlyDigits(raw);
    if (!canStillBeADate(next)) return; // refuse the keystroke outright
    setDigits(next);
    commit(next);
  };

  const handlePick = (e) => {
    const next = isoToDigits(e.target.value);
    setDigits(next);
    commit(next);
  };

  return (
    <>
      <span className={`date-field${unfinished ? ' is-unfinished' : ''}`}>
        <input
          {...rest}
          type="text"
          inputMode="numeric"
          // The digits read left-to-right even inside the RTL layout; without this the
          // day and year swap places on screen while the value stays correct.
          dir="ltr"
          placeholder={he.common.datePlaceholder}
          value={format(digits)}
          onChange={handleType}
          onFocus={(e) => e.target.select()}
          aria-invalid={unfinished || undefined}
        />
        {/* The native picker, kept for anyone who prefers it. Rendered transparent over
            the calendar glyph rather than driven by showPicker(), which not every
            browser we might sit in front of exposes. */}
        <span className="date-picker" title={he.common.openCalendar}>
          <input type="date" value={iso || ''} onChange={handlePick} tabIndex={-1} />
        </span>
      </span>
      {unfinished && (
        <span className="field-error">
          {state === 'incomplete' ? he.common.dateIncomplete : he.common.dateInvalid}
        </span>
      )}
    </>
  );
}

export default DateControl;
